import MoneyDisplay from 'components/util/MoneyDisplay';
import { getInvoiceDeltaAmounts } from 'pages/invoices/util/invoiceUtil';
import { FormattedMessage } from 'react-intl';
import { InvoiceData, InvoiceTotals } from 'types/invoices';
import { TOTALS_BASE_VALUE, TOTALS_LABEL_CELL, TOTALS_VALUE_CELL } from './InvoiceTotalsCard';

interface Props {
    invoice: InvoiceData;
    invoiceTotals?: InvoiceTotals;
}

const InvoiceTotalsCorrections = ({ invoice, invoiceTotals }: Props) => {
    const paymentCurrencyTypeCode = invoiceTotals?.paymentCurrency.currencyTypeCode;
    const baseCurrencyTypeCode = invoiceTotals?.baseCurrency.currencyTypeCode;
    const showBaseCurrency = baseCurrencyTypeCode !== paymentCurrencyTypeCode;

    const {
        deltaFeesPaymentCurrency,
        deltaExpensesPaymentCurrency,
        deltaTotalWithoutVatPaymentCurrency,
        deltaVatPaymentCurrency,
        deltaTotalWithVatPaymentCurrency,
        deltaFeesBaseCurrency,
        deltaExpensesBaseCurrency,
        deltaTotalWithoutVatBaseCurrency,
        deltaVatBaseCurrency,
        deltaTotalWithVatBaseCurrency,
    } = getInvoiceDeltaAmounts(invoice.deltaAmountsJson);

    return (
        <>
            {/* Fees credit amount (if not zero) */}
            {deltaFeesPaymentCurrency !== 0 ? (
                <>
                    <tr key="delta-fees">
                        <td className={TOTALS_LABEL_CELL}>
                            <FormattedMessage id="invoices.totals-tab.delta-fees" />
                        </td>
                        <td className={TOTALS_VALUE_CELL}>
                            <MoneyDisplay amount={deltaFeesPaymentCurrency} currency={paymentCurrencyTypeCode} />
                        </td>
                    </tr>
                    {showBaseCurrency ? (
                        <tr key="delta-fees-base-currency">
                            <td />
                            <td className={TOTALS_VALUE_CELL}>
                                <span className={TOTALS_BASE_VALUE}>
                                    <MoneyDisplay amount={deltaFeesBaseCurrency} currency={baseCurrencyTypeCode} />
                                </span>
                            </td>
                        </tr>
                    ) : null}
                </>
            ) : null}

            {/* Expenses credit amount (if not zero) */}
            {deltaExpensesPaymentCurrency !== 0 ? (
                <>
                    <tr key="delta-expenses">
                        <td className={TOTALS_LABEL_CELL}>
                            <FormattedMessage id="invoices.totals-tab.delta-expenses" />
                        </td>
                        <td className={TOTALS_VALUE_CELL}>
                            <MoneyDisplay amount={deltaExpensesPaymentCurrency} currency={paymentCurrencyTypeCode} />
                        </td>
                    </tr>
                    {showBaseCurrency ? (
                        <tr key="delta-expenses-base-currency">
                            <td />
                            <td className={TOTALS_VALUE_CELL}>
                                <span className={TOTALS_BASE_VALUE}>
                                    <MoneyDisplay amount={deltaExpensesBaseCurrency} currency={baseCurrencyTypeCode} />
                                </span>
                            </td>
                        </tr>
                    ) : null}
                </>
            ) : null}

            {/* Credit subtotal without VAT */}
            {deltaTotalWithoutVatPaymentCurrency !== 0 ? (
                <>
                    <tr key="delta-subtotal-wo-vat">
                        <td className={TOTALS_LABEL_CELL}>
                            <FormattedMessage id="invoices.totals-tab.delta-subtotal-wo-vat" />
                        </td>
                        <td className={TOTALS_VALUE_CELL}>
                            <MoneyDisplay
                                amount={deltaTotalWithoutVatPaymentCurrency}
                                currency={paymentCurrencyTypeCode}
                            />
                        </td>
                    </tr>
                    {showBaseCurrency ? (
                        <tr key="delta-subtotal-wo-vat-base-currency">
                            <td />
                            <td className={TOTALS_VALUE_CELL}>
                                <span className={TOTALS_BASE_VALUE}>
                                    <MoneyDisplay
                                        amount={deltaTotalWithoutVatBaseCurrency}
                                        currency={baseCurrencyTypeCode}
                                    />
                                </span>
                            </td>
                        </tr>
                    ) : null}
                </>
            ) : null}

            {/* Credit VAT */}
            {deltaVatPaymentCurrency !== 0 ? (
                <>
                    <tr key="delta-vat">
                        <td className={TOTALS_LABEL_CELL}>
                            <FormattedMessage id="invoices.totals-tab.delta-vat" />
                        </td>
                        <td className={TOTALS_VALUE_CELL}>
                            <MoneyDisplay amount={deltaVatPaymentCurrency} currency={paymentCurrencyTypeCode} />
                        </td>
                    </tr>
                    {showBaseCurrency ? (
                        <tr key="delta-vat-base-currency">
                            <td />
                            <td className={TOTALS_VALUE_CELL}>
                                <span className={TOTALS_BASE_VALUE}>
                                    <MoneyDisplay amount={deltaVatBaseCurrency} currency={baseCurrencyTypeCode} />
                                </span>
                            </td>
                        </tr>
                    ) : null}
                </>
            ) : null}

            {/* Credit subtotal with VAT */}
            {deltaTotalWithVatPaymentCurrency !== 0 ? (
                <>
                    <tr key="delta-subtotal-with-vat">
                        <td className={TOTALS_LABEL_CELL}>
                            <FormattedMessage id="invoices.totals-tab.delta-subtotal-with-vat" />
                        </td>
                        <td className={TOTALS_VALUE_CELL}>
                            <MoneyDisplay
                                amount={deltaTotalWithVatPaymentCurrency}
                                currency={paymentCurrencyTypeCode}
                            />
                        </td>
                    </tr>
                    {showBaseCurrency ? (
                        <tr key="delta-subtotal-with-vat-base-currency">
                            <td />
                            <td className={TOTALS_VALUE_CELL}>
                                <span className={TOTALS_BASE_VALUE}>
                                    <MoneyDisplay
                                        amount={deltaTotalWithVatBaseCurrency}
                                        currency={baseCurrencyTypeCode}
                                    />
                                </span>
                            </td>
                        </tr>
                    ) : null}
                </>
            ) : null}

            {/* Spacing row between corrections and current totals */}
            <tr className="h-6" key="correction-spacer">
                <td colSpan={2} />
            </tr>
        </>
    );
};

export default InvoiceTotalsCorrections;
